import {
  Box,
  Button,
  Card,
  CardHeader,
  Chip,
  Container,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { TOKEN_KEY, url, imgUrl } from 'src/Helpers/utils/Constant';
import { TableLoader, TableNotFound } from 'src/Utils/TableLoader';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import { Edit } from '@mui/icons-material';
import DeleteConfirmation from 'src/Utils/DeleteConfirmation';
import ModalsWrapper from 'src/components/Modals/ModalsWrapper';
import CreateWebsitePopup from './CreateWebsitePopup';
import UpdateWebsitePopup from './UpdateWebsitePopup';
import { IWebsitePopup } from './types';
import Toaster from 'src/Toaster/Toaster';
import dayjs from 'dayjs';

function WebsitePopup() {
  const theme = useTheme();
  const Toast = Toaster();

  const [loading, setLoading] = useState(false);
  const [popup, setPopup] = useState<IWebsitePopup | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const token = localStorage.getItem(TOKEN_KEY);

  // ▶ Create Modal
  const [createOpen, setCreateOpen] = useState(false);

  // ▶ Edit Modal
  const [editOpen, setEditOpen] = useState(false);
  const [popupToUpdate, setPopupToUpdate] = useState<IWebsitePopup | null>(null);

  // ▶ Delete Modal
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Fetch single popup (API returns a single object, not an array)
  useEffect(() => {
    const fetchPopup = async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(`${url}/api/v1/admin/website-popup`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (data.success && data.data) {
          setPopup(data.data);
        } else {
          setPopup(null);
        }
      } catch {
        setPopup(null);
      } finally {
        setLoading(false);
      }
    };
    fetchPopup();
  }, [refreshTrigger, token]);

  // Delete
  const handleDelete = async () => {
    if (!popup?.id) return;
    try {
      setDeleteLoading(true);
      const { data } = await axios.delete(
        `${url}/api/v1/admin/website-popup`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data.success) {
        Toast.fire({ icon: 'success', title: data.message || 'Website popup deleted!' });
        setRefreshTrigger((prev) => prev + 1);
        setDeleteOpen(false);
      } else {
        Toast.fire({ icon: 'error', title: data.message || 'Failed to delete.' });
      }
    } catch (err: any) {
      Toast.fire({ icon: 'error', title: err?.response?.data?.message || 'Error deleting popup.' });
    } finally {
      setDeleteLoading(false);
    }
  };


  return (
    <Container maxWidth='xl'>
      <Card>
        <CardHeader
          action={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            
                <Button
                  onClick={() => setCreateOpen(true)}
                  variant='contained'
                  sx={{ ml: 2 }}
                >
                  Create Website Popup
                </Button>
            
            </Box>
          }
          title='Website Popup'
        />
        <Divider />

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Image</TableCell>
                <TableCell>Image Alt Text</TableCell>
                <TableCell>Website Link</TableCell>
                <TableCell align='center'>Start Date</TableCell>
                <TableCell align='center'>End Date</TableCell>
                <TableCell align='center'>Status</TableCell>
                <TableCell align='center'>Action</TableCell>
              </TableRow>
            </TableHead>

            {loading ? (
              <TableLoader />
            ) : popup ? (
              <TableBody>
                <TableRow hover>
                  {/* Image */}
                  <TableCell>
                    {popup.image ? (
                      <Box
                        component='img'
                        src={`${imgUrl}/${popup.image}`}
                        alt={popup.alt_text}
                        sx={{
                          width: 70,
                          height: 50,
                          borderRadius: 1,
                          objectFit: 'cover',
                          boxShadow: 1,
                        }}
                      />
                    ) : (
                      <Typography variant='body2' color='text.secondary'>
                        No Image
                      </Typography>
                    )}
                  </TableCell>

                  {/* Alt Text */}
                  <TableCell>
                    <Typography variant='body1' color='text.primary' noWrap>
                      {popup.alt_text}
                    </Typography>
                  </TableCell>

                  {/* Website Link */}
                  <TableCell>
                    <Typography
                      variant='body2'
                      component='a'
                      href={popup.website_link}
                      target='_blank'
                      rel='noopener noreferrer'
                      sx={{
                        color: theme.palette.primary.main,
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' },
                        wordBreak: 'break-all',
                        maxWidth: 220,
                        display: 'block',
                      }}
                    >
                      {popup.website_link}
                    </Typography>
                  </TableCell>

                  {/* Start Date */}
                  <TableCell align='center'>
                    <Typography variant='body2' color='text.primary' noWrap>
                      {popup.start_date&&dayjs(popup.start_date).format('DD-MMM-YYYY')}
                    </Typography>
                  </TableCell>

                  {/* End Date */}
                  <TableCell align='center'>
                    <Typography variant='body2' color='text.primary' noWrap>
                      {popup.end_date&&dayjs(popup.end_date).format('DD-MMM-YYYY')}
                    </Typography>
                  </TableCell>

                  {/* Status */}
                  <TableCell align='center'>
                    <Chip
                      label={Number(popup.status) === 1 ? 'Active' : 'Inactive'}
                      color={Number(popup.status) === 1 ? 'success' : 'error'}
                      size='small'
                      sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}
                    />
                  </TableCell>

                  {/* Actions */}
                  <TableCell align='center'>
                    <Tooltip title='Edit' arrow>
                      <IconButton
                        onClick={() => {
                          setPopupToUpdate(popup);
                          setEditOpen(true);
                        }}
                        sx={{
                          '&:hover': { background: theme.colors.primary.lighter },
                          color: theme.palette.primary.main,
                          mr: 1,
                        }}
                        size='small'
                      >
                        <Edit fontSize='small' />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title='Delete' arrow>
                      <IconButton
                        onClick={() => setDeleteOpen(true)}
                        sx={{
                          '&:hover': { background: theme.colors.error.lighter },
                          color: theme.palette.error.main,
                        }}
                        size='small'
                      >
                        <DeleteTwoToneIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <TableNotFound />
            )}
          </Table>
        </TableContainer>

        {/* CREATE MODAL */}
        <ModalsWrapper
          setShowModal={setCreateOpen}
          showModal={createOpen}
          modalData={{ title: 'Create Website Popup' }}
          maxWidth='sm'
        >
          <CreateWebsitePopup
            handleClose={() => setCreateOpen(false)}
            setRefreshTrigger={setRefreshTrigger}
          />
        </ModalsWrapper>

        {/* EDIT MODAL */}
        <ModalsWrapper
          setShowModal={(val: boolean) => {
            setEditOpen(val);
            if (!val) setPopupToUpdate(null);
          }}
          showModal={editOpen}
          modalData={{ title: 'Update Website Popup' }}
          maxWidth='sm'
        >
          {popupToUpdate && (
            <UpdateWebsitePopup
              key={popupToUpdate.id}
              popup={popupToUpdate}
              handleClose={() => {
                setEditOpen(false);
                setPopupToUpdate(null);
              }}
              setRefreshTrigger={setRefreshTrigger}
            />
          )}
        </ModalsWrapper>

        {/* DELETE CONFIRMATION */}
        <DeleteConfirmation
          open={deleteOpen}
          handleClose={() => setDeleteOpen(false)}
          text='Are you sure you want to delete this website popup?'
          handleDeleteFun={handleDelete}
          loading={deleteLoading}
        />
      </Card>
    </Container>
  );
}

export default WebsitePopup;
